import type { OverlayParams } from './types';
import type { TextPosition } from '../../../widgets/reel-create-form/lib/buildRenderTasks';

// The template fields the overlay needs. Both Template (entity) and the editor's
// TemplateFormData satisfy this shape (same field names).
export interface OverlayTemplateFields {
  backgroundColor?: string;
  backgroundAlpha?: number;
  textColor?: string;
  textColorSecondary?: string;
  useTwoColors?: boolean;
  fontFamily?: string;
  fontUrl?: string;
  fontVariationSettings?: Record<string, number>;
  textUppercase?: boolean;
  textBoxStyle?: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
  textBoxAngle?: number;
  textBoxRadius?: number;
  textBoxPadding?: number;
  textBoxPaddingTop?: number;
  textBoxPaddingLeft?: number;
  textBoxPaddingRight?: number;
  textShadowEnabled?: boolean;
  textShadowColor?: string;
  textShadowBlur?: number;
  textShadowWidth?: number;
  textShadowOpacity?: number;
  textSoftShadowEnabled?: boolean;
  textSoftShadowColor?: string;
  textSoftShadowBlur?: number;
  textSoftShadowOffsetX?: number;
  textSoftShadowOffsetY?: number;
  textSoftShadowOpacity?: number;
  logoPosition?: OverlayParams['logoPosition'];
  frameUrl?: string;
  reelBackgroundUrl?: string;
  textAlign?: 'left' | 'center' | 'right';
  reelTextCenterX?: number;
  logoSize?: number;
  textLineSpacing?: number;
}

async function fetchBuf(url?: string): Promise<ArrayBuffer | null> {
  if (!url) return null;
  return fetch(url).then((r) => (r.ok ? r.arrayBuffer() : null)).catch(() => null);
}

/**
 * Builds the OverlayParams the webcodecs worker consumes, from template fields.
 * Logo is passed in by the caller (per-account upload File in publish, or template
 * logoUrl in the editor) — fetched into an ArrayBuffer by the caller. Frame and reel
 * background are fetched here from their template URLs.
 */
export async function buildOverlayParams(args: {
  template: OverlayTemplateFields;
  text: string;
  textPosition: TextPosition;
  fontSize: number;
  logoArrayBuffer: ArrayBuffer | null;
}): Promise<OverlayParams> {
  const t = args.template;
  const [frameArrayBuffer, reelBackgroundArrayBuffer] = await Promise.all([
    fetchBuf(t.frameUrl),
    fetchBuf(t.reelBackgroundUrl),
  ]);
  return {
    text: args.text,
    logoArrayBuffer: args.logoArrayBuffer,
    frameArrayBuffer,
    reelBackgroundArrayBuffer,
    textPosition: args.textPosition,
    backgroundColor: t.backgroundColor ?? '#000000',
    backgroundAlpha: t.backgroundAlpha,
    textBoxStyle: t.textBoxStyle,
    textBoxAngle: t.textBoxAngle,
    textBoxRadius: t.textBoxRadius,
    textBoxPadding: t.textBoxPadding,
    textBoxPaddingTop: t.textBoxPaddingTop,
    textBoxPaddingLeft: t.textBoxPaddingLeft,
    textBoxPaddingRight: t.textBoxPaddingRight,
    textColor: t.textColor ?? '#ffffff',
    textShadowEnabled: t.textShadowEnabled,
    textShadowColor: t.textShadowColor,
    textShadowBlur: t.textShadowBlur,
    textShadowWidth: t.textShadowWidth,
    textShadowOpacity: t.textShadowOpacity,
    textSoftShadowEnabled: t.textSoftShadowEnabled,
    textSoftShadowColor: t.textSoftShadowColor,
    textSoftShadowBlur: t.textSoftShadowBlur,
    textSoftShadowOffsetX: t.textSoftShadowOffsetX,
    textSoftShadowOffsetY: t.textSoftShadowOffsetY,
    textSoftShadowOpacity: t.textSoftShadowOpacity,
    fontFamily: t.fontFamily ?? 'Arial',
    fontUrl: t.fontUrl,
    textUppercase: t.textUppercase ?? false,
    useTwoColors: t.useTwoColors,
    textColorSecondary: t.textColorSecondary,
    fontVariationSettings: t.fontVariationSettings,
    logoPosition: t.logoPosition,
    textAlign: t.textAlign,
    hCenterX: t.reelTextCenterX ?? 0,
    logoSize: t.logoSize,
    lineSpacing: t.textLineSpacing,
    textDuration: 'full',
    fontSize: args.fontSize,
  };
}
